import { validatePathParams } from '@/utils/hooks/api/safePathParams';
import {
  buildApiEndpoint,
  extractBody,
  extractError,
  extractErrorData,
  extractErrorMessage,
} from '@/utils/hooks/api/typescript/methodHelpers';
import {
  isActivityLogRateLimitError,
  markActivityLogRateLimitedFromError,
} from '@/utils/activityLogging/activityLogClientRateLimit';
import { parseErrorMessage } from '@/utils/hooks/api/vercel/helpers/parseErrorMessage';
import { getAPIRoute } from '@/utils/routing/apiRoutes';
import { useSnackBarContext } from '@/utils/snackbar';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';

export interface UsePostConfig {
  successMessage?: string;
  errorMessage?: string;
  errorMessageFallback?: string;
  errorMessagePrefix?: string;
  ping?: boolean;
  /** When `ping` is true, stop polling after this many ping requests (each followed by a 3s wait if still incomplete). Omit for no limit. */
  maxAttempts?: number;
  pathParams?: Record<string, string | number | undefined>;
  pathParamValidators?: Record<string, RegExp>;
  /** When true, 429 `ACTIVITY_LOG_RATE_LIMITED` skips console.error and error snack. */
  suppressActivityLogRateLimitErrors?: boolean;
}

export interface UsePostResult<T = unknown, R = unknown> {
  request: UseMutationResult<AxiosResponse<R>, unknown, T>;
  fire: (data: T) => void;
  fireAsync: (data: T) => Promise<AxiosResponse<R>>;
  hasBeenPreviouslyLoaded: boolean;
  persistentData: R | undefined;
  isLoading: boolean;
  isError: boolean;
  data: R | undefined;
  error: unknown;
  errorMessage: string | undefined;
  errorData: Record<string, unknown> | undefined;
  reset: () => void;
  isSuccess: boolean;
  status: string;
  pingAndWait: () => void;
  pingResponse: unknown;
}

export default function usePost<T = unknown, R = unknown>(
  endpoint: string,
  config: UsePostConfig = {}
): UsePostResult<T, R> {
  const [pinging, setPinging] = useState(false);
  const [requestData, setRequestData] = useState<T | undefined>(undefined);
  const [pingResponse, setPingResponse] = useState<unknown>(undefined);
  const [pingError, setPingError] = useState<unknown>(null);
  const [hasBeenPreviouslyLoaded, setHasBeenPreviouslyLoaded] = useState(false);
  const [persistentData, setPersistentData] = useState<R | undefined>(
    undefined
  );

  const { updateSnack } = useSnackBarContext();

  let baseEndpoint = endpoint;
  let pathParamsValid = true;

  if (config.pathParams) {
    const { valid, path } = validatePathParams(
      endpoint,
      config.pathParams,
      config.pathParamValidators
    );
    pathParamsValid = valid;
    if (valid && path) {
      baseEndpoint = path;
    }
  }

  const fullEndpoint = buildApiEndpoint(getAPIRoute(baseEndpoint));

  const mutation = useMutation<AxiosResponse<R>, unknown, T>((data: T) => {
    if (!pathParamsValid) {
      return Promise.reject(
        new Error('Invalid path parameters prevented request')
      );
    }
    setRequestData(data);
    return axios.post<R>(fullEndpoint, data);
  });

  // handle snack for error
  useEffect(() => {
    if (!mutation.isError) return;

    if (
      config.suppressActivityLogRateLimitErrors &&
      isActivityLogRateLimitError(mutation.error)
    ) {
      markActivityLogRateLimitedFromError(mutation.error);
      return;
    }

    console.error('Error in usePost: ', mutation.error);

    const snackMessage = parseErrorMessage(config, mutation);

    updateSnack({
      severity: 'error',
      message: snackMessage,
    });
  }, [mutation.isError]);

  // Should trigger everytime we get a success or error response. Set persistent data
  // and previously loaded flag.
  useEffect(() => {
    if (!mutation.isSuccess && !mutation.isError) return;
    setHasBeenPreviouslyLoaded(true);
    setPersistentData(extractBody<R>(mutation.data));
  }, [mutation.isSuccess, mutation.isError]);

  useEffect(() => {
    if (!pinging && !pingError && mutation.isSuccess) {
      if (config?.successMessage) {
        updateSnack({
          severity: 'success',
          message: config.successMessage,
        });
      }
    }
  }, [pinging, mutation.isSuccess]);

  useEffect(() => {
    if (!config.ping) return;
    if (mutation.isLoading) {
      setPinging(true);
      setPingError(null);
    }
    if (pinging && !mutation.isLoading) pingAndWait();
  }, [mutation.isLoading]);

  useEffect(() => setPinging(false), [mutation.error]);

  const maxPingAttempts =
    typeof config.maxAttempts === 'number' && config.maxAttempts > 0
      ? config.maxAttempts
      : null;

  function pingAndWait(attemptNumber = 1) {
    if (!pinging) return;
    axios
      .post(fullEndpoint + '?ping=true', requestData)
      .then((resp) => {
        setPingResponse(resp.data);
        if (resp.data.completed) {
          setPinging(false);
        } else if (
          maxPingAttempts != null &&
          attemptNumber >= maxPingAttempts
        ) {
          setPinging(false);
          const timeoutMessage = `The operation did not complete after ${maxPingAttempts} status checks. Please refresh and try again.`;
          setPingError(new Error(timeoutMessage));
          updateSnack({
            severity: 'error',
            message: timeoutMessage,
          });
        } else {
          setTimeout(() => pingAndWait(attemptNumber + 1), 3000);
        }
      })
      .catch((err) => {
        setPingError(err);
        setPinging(false);
      });
  }

  const extractedError = extractError(mutation.error as AxiosError);
  const errorMessage =
    extractErrorMessage(extractedError) ?? extractErrorMessage(pingError);

  return {
    request: mutation,

    fire: mutation.mutate,
    fireAsync: mutation.mutateAsync,

    isLoading: pinging || mutation.isLoading,
    hasBeenPreviouslyLoaded: hasBeenPreviouslyLoaded,

    isError: Boolean(mutation.isError || pingError),
    error: extractedError || pingError,
    errorMessage,
    errorData: extractErrorData((mutation.error as AxiosError) ?? undefined),

    persistentData: persistentData,
    data: extractBody<R>(mutation.data),

    reset: mutation.reset,
    isSuccess: !pinging && mutation.isSuccess,
    status: mutation.status,
    pingAndWait,
    pingResponse,
  };
}
